import { useMemo } from 'react';
import styled from 'styled-components';
import Image from 'next/image';
import i18n from 'src/utils/translate';
import { generateAvatarLinkFromHash } from 'src/utils/linkUtils';
import { stripBBTags } from 'src/utils/string';
import config from 'config/config';
import { clickEventsPost } from 'src/modules/shared/resultsListNew/footfallEvents';
import PostItemMeta from 'src/modules/shared/resultsList/components/postItemMeta';
import { Body, MetaTextWrapper } from 'styles-js/postItem';
import PostLink from 'src/modules/shared/links/postLink';
import ImagePlaceMarker from 'src/modules/community/singlePost/components/imagePlaceMarker';
type IsEvent = {
  isOnAvatarClick?: boolean;
  isOnCommunityClick?: boolean;
  isOnUsernameClick?: boolean;
  isOnTotalResponsesClick?: boolean; // not in replies
  isOnFirstToReplyClick?: boolean; // not in replies
  isOnBodyClick?: boolean;
  isOnMetaTextClick?: boolean;
};
type PostItemProps = {
  author: {
    userImageHash: string;
    username: string;
  };
  bodySnippet: string;
  community: {
    communityId: number;
    name: string;
    slug: string;
  };
  clickPostMetadata: {
    section: string;
    element?: string;
    origin?: string;
  };
  dateCreated: string;
  isLocked: boolean;
  isPostFootfallEvents?: IsEvent;
  isPrivate: boolean;
  postId: number;
  postImageHash: string;
  postTypeId: number;
  title: string;
  totalResponses: number;
  urlEncodedTitle: string;
};
export default function PostItem({
  author,
  bodySnippet,
  clickPostMetadata,
  community,
  dateCreated,
  isLocked,
  isPrivate,
  isPostFootfallEvents,
  postId,
  postImageHash,
  postTypeId,
  title,
  totalResponses,
  urlEncodedTitle
}: PostItemProps) {
  const cleanPostSnippet = useMemo(() => bodySnippet && stripBBTags(bodySnippet), [bodySnippet]);
  const clickEvents = clickEventsPost({
    clickMetadata: clickPostMetadata,
    ...isPostFootfallEvents
  });
  return <>
      <PostItemMeta authorAvatarUrl={generateAvatarLinkFromHash(author.userImageHash)} authorUsername={author.username} community={community} dateCreated={dateCreated} hasPopularBadge={totalResponses >= 20 ? true : false} onAvatarClick={clickEvents?.onAvatarClick} onCommunityClick={clickEvents?.onCommunityClick} onUsernameClick={clickEvents?.onUsernameClick} data-sentry-element="PostItemMeta" data-sentry-source-file="postItem.tsx" />
      {postImageHash && <ImagePlaceMarker communityId={community.communityId}>
          <ImageContainer>
            <Image fill alt="User submitted image" data-testid="post-image" src={`${config.cdn.postImg}${postImageHash}.jpg`} unoptimized={true} />
          </ImageContainer>
        </ImagePlaceMarker>}
      {bodySnippet && <PostLink communitySlug={community.slug} isPrivate={isPrivate} postId={postId} postTypeId={postTypeId} urlEncodedTitle={urlEncodedTitle} onClick={clickEvents?.onBodyClick}>
          <Body>{cleanPostSnippet}</Body>
        </PostLink>}
      <ReplyInfo data-sentry-element="ReplyInfo" data-sentry-source-file="postItem.tsx">
        {totalResponses > 0 ? <PostLink anchorToResponses={true} communitySlug={community.slug} isPrivate={isPrivate} postId={postId} postTypeId={postTypeId} urlEncodedTitle={urlEncodedTitle} onClick={clickEvents?.onTotalResponsesClick}>
            <GreyText aria-label={i18n.t(`Read all replies for "${title}"`)}>
              {totalResponses} {totalResponses === 1 ? i18n.t('reply') : i18n.t('replies')}
            </GreyText>
          </PostLink> : !isLocked && <PostLink anchorToResponses={true} communitySlug={community.slug} isPrivate={isPrivate} postId={postId} postTypeId={postTypeId} urlEncodedTitle={urlEncodedTitle} onClick={clickEvents?.onFirstToReplyClick}>
              {i18n.t('Be the first one to reply')}
            </PostLink>}
      </ReplyInfo>
    </>;
}
const ImageContainer = styled.div`
  margin-top: 10px;
  height: 200px;
  position: relative;
  img {
    object-fit: contain; // preserve aspect ratio
    object-position: left;
  }
`;
const ReplyInfo = styled(MetaTextWrapper)`
  margin: 10px 0 0;
`;
const GreyText = styled.span`
  color: ${props => props.theme.colorTextLight};
  transition: all 0.5s ease;
  &:hover, &:focus, &:active {
    color: ${({
  theme
}) => theme.colorBlack};
  }
`;